import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import { namesToFullName } from '@nova/core';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { reactiveOption } from '@satellite/composables/presenterV2ReactiveOption';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type {
  PresenterV2DisplayConfigOptions,
  PresenterV2DisplayOverrideProps
} from '@satellite/composables/presenterV2DisplayOptions';
import type { CarrierUserDetails } from './carrierPresenterV2Shared';

export type CarrierDisplayField = 'name' | 'email' | 'phone' | 'companyName' | 'companyScac';

export function getCarrierName(carrier: Maybe<CarrierUserDetails>): string | null {
  if (carrier?.name) {
    return carrier.name;
  }

  if (carrier?.firstName || carrier?.lastName) {
    return namesToFullName(carrier.firstName, carrier.lastName);
  }

  return null;
}

export function getCarrierEmail(carrier: Maybe<CarrierUserDetails>): string | null {
  return carrier?.email ?? null;
}

export function getCarrierPhone(carrier: Maybe<CarrierUserDetails>): string {
  return carrier?.phone || '';
}

export function getCarrierCompanyName(carrier: Maybe<CarrierUserDetails>): string | null {
  return carrier?.company?.name ?? null;
}

export function getCarrierCompanyScac(carrier: Maybe<CarrierUserDetails>): string | null {
  return carrier?.company?.scac ?? null;
}

export interface UseCarrierDisplayV2Options extends PresenterV2DisplayConfigOptions {
  nameProps?: PresenterV2DisplayOverrideProps;
  emailProps?: PresenterV2DisplayOverrideProps;
  phoneProps?: PresenterV2DisplayOverrideProps;
  companyNameProps?: PresenterV2DisplayOverrideProps;
  companyScacProps?: PresenterV2DisplayOverrideProps;
}

export function useCarrierDisplayV2(
  carrier: Ref<CarrierUserDetails | null> | ComputedRef<CarrierUserDetails | null>,
  options: UseCarrierDisplayV2Options = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterV2Mode.Live);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const carrierName = computed(() => getCarrierName(carrier.value));
  const carrierEmail = computed(() => getCarrierEmail(carrier.value));
  const carrierPhone = computed(() => getCarrierPhone(carrier.value));
  const companyName = computed(() => getCarrierCompanyName(carrier.value));
  const companyScac = computed(() => getCarrierCompanyScac(carrier.value));

  const carrierNameAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-name' }, toValue(options.nameProps) ?? {})
  );
  const carrierEmailAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-email' }, toValue(options.emailProps) ?? {})
  );
  const carrierPhoneAttrs = computed(() =>
    mergeProps({ 'data-testid': 'carrier-presenter-v2-phone' }, toValue(options.phoneProps) ?? {})
  );
  const companyNameAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'company-presenter-v2-company-name' },
      toValue(options.companyNameProps) ?? {}
    )
  );
  const companyScacAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'company-presenter-v2-company-scac' },
      toValue(options.companyScacProps) ?? {}
    )
  );

  const unknownCompanyMessage = computed(() => t('core.yard.messages.unknownCompany'));

  return reactive({
    carrier,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    carrierName,
    carrierEmail,
    carrierPhone,
    companyName,
    companyScac,
    carrierNameAttrs,
    carrierEmailAttrs,
    carrierPhoneAttrs,
    companyNameAttrs,
    companyScacAttrs,
    unknownCompanyMessage
  });
}

export type CarrierDisplayV2Data = ReturnType<typeof useCarrierDisplayV2>;
